import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { useAlgoliaAnalytics, DataLayer, WebPage } from '@grandvisionhq/analytics';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { MediaQuery } from '@grandvisionhq/common';
import { ProductDetailActions } from '@grandvisionhq/fun-products';
import { useFeatureToggles } from '@grandvisionhq/state';
import { monetate } from '@grandvisionhq/svc-monetate';
import { calculateHeaderHeight, getAttrValue, isModifiedClickEvent } from '@grandvisionhq/utils';
import Frames from './Frames/Frames.js';
import Sunglasses from './Sunglasses/Sunglasses.js';
import { useProductDetailContext } from './Context/ProductDetailContext.js';
import { ProductImageGalleryContainer } from '../ProductImageGallery/ProductImageGalleryContainer.js';
import { LensBundleProvider } from '../LensBundles/context.js';
import Lenses from './Lenses/LensesContainer.js';
import Solutions from './Solutions/SolutionsContainer.js';
import { ReadyReadersContainer } from './ReadyReaders/ReadyReadersContainer.js';
import Accessories from './Accessories/AccessoriesContainer.js';

const ProductDetail = ({ sizeGuideDocument , colorFeatureIcons , children , isPrescriptionExtraInfoEnabled , allowBackOrder , maxOrderableQuantity , deliveryResolver , deliveryResolverFactory , prescriptionModalContent , opticianForm , productTypeUsps , configuratorDocument , formatProductUrl , specificationConfiguration , prescriptionInfoAdditionalContent , secondaryCallToAction , vtoBanner , disclaimer , brandPromotions , sspConfig ,  })=>{
    const { routes , activeSku , activeVariant , product , productType , dispatch , renderAddToCartButton ,  } = useProductDetailContext();
    const isMobile = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const algoliaAnalytics = useAlgoliaAnalytics();
    const { queryId: storedQueryId  } = (algoliaAnalytics === null || algoliaAnalytics === void 0 ? void 0 : algoliaAnalytics.algoliaAnalyticsData) || {};
    const algoliaQueryId = React.useRef(storedQueryId);
    const { isMonetateProductDataEnabled  } = useFeatureToggles();
    const containerRef = React.useRef(null);
    const [ctaRef, ctaInview] = useInView();
    const showStickyButton = isMobile && !ctaInview;
    const [topOffset, setTopOffset] = React.useState(0);
    const [calculatedAddToCartPrice1, setCalculatedAddToCartPrice] = React.useState(null);
    React.useEffect(()=>{
        DataLayer.pushPageTypeToDataLayer('product detail');
    // Disabling exhaustive deps because we want to run the tracking event exactly once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(()=>{
        monetate().trackProductDetail(isMonetateProductDataEnabled ? [
            {
                sku: activeVariant.sku,
                productId: product.id
            }, 
        ] : []);
    // Disabling exhaustive deps because we want to run the tracking event exactly once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        activeVariant
    ]);
    React.useEffect(()=>{
        if (storedQueryId) {
            algoliaQueryId.current = storedQueryId;
        }
    }, [
        storedQueryId
    ]);
    React.useEffect(()=>{
        // make sure the queryId will not be used when a user doesn't click the CTA. For example: user clicks on a product suggestion and clicks the CTA there.
        algoliaQueryId.current && algoliaAnalytics.clearQueryId();
    }, [
        algoliaQueryId.current
    ]);
    React.useEffect(()=>{
        // set offset based on headerHeight
        setTopOffset(calculateHeaderHeight());
    }, []);
    const ean1 = React.useMemo(()=>{
        const ean = getAttrValue(product.masterVariant, 'supplierEAN');
        if (typeof ean === 'string') {
            return ean;
        }
        return undefined;
    }, [
        product
    ]);
    const ImageGallery = React.useMemo(()=>/*#__PURE__*/ React.createElement(ProductImageGalleryContainer, {
            activeVariant: activeVariant,
            product: product,
            vtoBanner: vtoBanner
        })
    , [
        activeVariant,
        product,
        vtoBanner
    ]);
    const handleVariantPick = React.useCallback((event, sku)=>{
        dispatch(ProductDetailActions.updateActiveVariant({
            sku
        }));
        const newUrl = formatProductUrl({
            sku,
            slug: product.slug,
            productType
        });
        // will pass the current queryString in the url of the variant picker
        const queryString = typeof window !== 'undefined' ? window.location.search : '';
        window.history.replaceState(null, '', `${newUrl}${queryString}`);
        // Allow customers to open another variant in a new tab or window, otherwise prevent navigation
        if (event && !isModifiedClickEvent(event)) {
            event.preventDefault();
        }
    }, [
        dispatch,
        formatProductUrl,
        product.slug,
        productType
    ]);
    /**
   * This callback is used in the Prescription Glasses configurator
   * to supply the full bundle price (Frame, LensType, Package, and Add-ons)
   * to the AddToCartNotification
   */ const handlePrescriptionGlassesPriceChange = React.useCallback((bundlePrice)=>{
        setCalculatedAddToCartPrice(bundlePrice);
    }, []);
    /**
   * This handler is called from the Lenses page whenever the
   * price changes from their prescription selection.
   * This price object is supplied to the AddToCartNotification
   */ const handleLensPriceChange = React.useCallback(({ calculatedPrice , calculatedDiscountPrice  })=>{
        const { originalPrice , discountedPrice  } = calculatedAddToCartPrice1 ? calculatedAddToCartPrice1 : {
            originalPrice: 0,
            discountedPrice: undefined
        };
        if (originalPrice !== calculatedPrice || discountedPrice !== calculatedDiscountPrice) {
            const calculatedAddToCartPrice = {
                originalPrice: calculatedPrice,
                discountedPrice: calculatedDiscountPrice
            };
            setCalculatedAddToCartPrice(calculatedAddToCartPrice);
        }
    }, []);
    /**
   * Sends a conversion event to Algolia
   */ const handleConversionEvent = React.useCallback(()=>{
        algoliaAnalytics.handleConversionEvent(activeSku, algoliaQueryId.current);
    }, [
        activeSku,
        algoliaAnalytics
    ]);
    if (!productType || !activeVariant) {
        return null;
    }
    const commonProps = {
        activeSku,
        ean: ean1,
        addToCartRef: ctaRef,
        dispatch,
        formatProductUrl,
        sizeGuideDocument,
        product,
        productTypeUsps,
        showStickyButton,
        specificationConfiguration,
        stickyTop: topOffset,
        onCTAClick: handleConversionEvent,
        renderAddToCartButton,
        handleConversionEvent,
        disclaimer
    };
    const variantUrl = formatProductUrl({
        productType: activeVariant.productType,
        sku: activeVariant.sku,
        slug: product.slug
    });
    const productUrl = `${variantUrl}#product`;
    const subscriptionProductSKU = getAttrValue(product.masterVariant, 'subscriptionProductSKU');
    const bookAppointmentRoute = routes['book-appointment'] || routes['appointment-book'];
    return /*#__PURE__*/ React.createElement("div", {
        className: "product-detail"
    }, /*#__PURE__*/ React.createElement("div", {
        ref: containerRef,
        className: "site-container product-detail__container"
    }, CommerceToolsApi.isLenses(activeVariant) && /*#__PURE__*/ React.createElement(LensBundleProvider, {
        subscriptionProductSKU: subscriptionProductSKU
    }, /*#__PURE__*/ React.createElement(Lenses, swcHelpers.extends({}, commonProps, {
        colorFeatureIcons: colorFeatureIcons,
        variant: activeVariant,
        onPriceChange: handleLensPriceChange,
        specificationConfiguration: specificationConfiguration,
        productImageGallery: ImageGallery,
        /**
               * Stock configurations props
               */ maxOrderableQuantity: maxOrderableQuantity,
        deliveryResolverFactory: deliveryResolverFactory,
        allowBackOrder: allowBackOrder,
        /**
               * Prescription props
               */ prescriptionModalContent: prescriptionModalContent,
        opticianForm: opticianForm,
        isPrescriptionExtraInfoEnabled: isPrescriptionExtraInfoEnabled,
        prescriptionInfoAdditionalContent: prescriptionInfoAdditionalContent,
        hasSubscriptions: !!subscriptionProductSKU
    }))) || CommerceToolsApi.isFrames(activeVariant) && /*#__PURE__*/ React.createElement(Frames, swcHelpers.extends({}, commonProps, {
        bookAppointmentRoute: bookAppointmentRoute,
        configuratorDocument: configuratorDocument,
        onPriceChange: handlePrescriptionGlassesPriceChange,
        variant: activeVariant,
        specificationConfiguration: specificationConfiguration,
        opticianForm: opticianForm,
        secondaryCallToAction: secondaryCallToAction,
        productImageGallery: ImageGallery,
        storesRoute: routes.stores,
        sspConfig: sspConfig
    }), children) || CommerceToolsApi.isSunglasses(activeVariant) && /*#__PURE__*/ React.createElement(Sunglasses, swcHelpers.extends({}, commonProps, {
        colorFeatureIcons: colorFeatureIcons,
        onPriceChange: handlePrescriptionGlassesPriceChange,
        configuratorDocument: configuratorDocument,
        opticianForm: opticianForm,
        variant: activeVariant,
        specificationConfiguration: specificationConfiguration,
        deliveryResolver: deliveryResolver,
        secondaryCallToAction: secondaryCallToAction,
        productImageGallery: ImageGallery,
        brandPromotions: brandPromotions,
        storesRoute: routes.stores,
        bookAppointmentRoute: bookAppointmentRoute
    })) || CommerceToolsApi.isSolutions(activeVariant) && /*#__PURE__*/ React.createElement(Solutions, swcHelpers.extends({}, commonProps, {
        variant: activeVariant,
        handleVariantPick: handleVariantPick,
        specificationConfiguration: specificationConfiguration,
        maxOrderableQuantity: maxOrderableQuantity,
        productImageGallery: ImageGallery
    })) || CommerceToolsApi.isReadyReaders(activeVariant) && /*#__PURE__*/ React.createElement(ReadyReadersContainer, swcHelpers.extends({}, commonProps, {
        formatProductUrl: formatProductUrl,
        handleConversionEvent: handleConversionEvent,
        handleVariantPick: handleVariantPick,
        productImageGallery: ImageGallery,
        specificationConfiguration: specificationConfiguration,
        variant: activeVariant,
        storesRoute: routes.stores
    })) || CommerceToolsApi.isAccessories(activeVariant) && /*#__PURE__*/ React.createElement(Accessories, swcHelpers.extends({}, commonProps, {
        formatProductUrl: formatProductUrl,
        handleConversionEvent: handleConversionEvent,
        productImageGallery: ImageGallery,
        specificationConfiguration: specificationConfiguration,
        variant: activeVariant,
        storesRoute: routes.stores
    }))), /*#__PURE__*/ React.createElement(WebPage, {
        url: productUrl,
        pageType: "ProductDetail",
        name: activeVariant.brand,
        description: activeVariant.description
    }));
};

export { ProductDetail };
//# sourceMappingURL=ProductDetail.js.map
